/**
 * Returns a partition of a 2D matrix (mtx) spanning x + w x y + h
 * Fills missing entries with null
 */
export const partition = (mtx, m1, n1, m2, n2) => {
  let result = []
  for(let i = m1; i < m2; i++) {
    if(mtx[i]) {
      const row = []
      for(let j = n1; j < n2; j++) {
        if(mtx[i][j] !== null) {
          row.push(mtx[i][j])
        } else {
          row.push(null)
        }
      }
      result.push(row)
    } else {
      result.push(Array(n2-n1).fill(null))
    }
  }
  return result
}

/**
 * Returns an (x, y) pair where an object with width and height (w, h)
 * can be placed in the matrix (mtx).
 * Valid coordinates are predicated on empty (null) values in supplied matrix
 */
export const gridfit = (mtx, w, h, maxw) => {
  for(let i = 0; i < mtx.length; i++) {
    const row = mtx[i]
    for(let j = 0; j < maxw; j++) {
      const col = row[j]
      if(col === null && j + w <= maxw) {
        const p = partition(mtx, i, j, i+h, j+w)
        const is_empty = p.reduce((acc, row) => {
          if(row.reduce((acc2, n) => {
            if(n !== null) acc2++
            return acc2
          }, 0)) acc++
          return acc
        }, 0) === 0

        if(is_empty) return [j, i]
      }
    }
  }
  return [0, mtx.length]
}

/**
 * Returns a matrix representation of a given component layout
 * @param layout An array of items of the form: {w, h, x, y, i}
 * @param cols The number of columns in the layout
 * @returns A matrix representation of the layout
 *
 * Ex.
 * const mat = matrixize({w:2, h:2, x:0, y:0, i:1}, {w:1, h:2, x:3, y:0, i:3}, 4)
 * console.log(mat_to_str(mat)) // [[1, 1, null, 3], [1, 1, null, 3]]
 */
export const matrixize = (layout, cols) => {
  return layout.reduce((mtx, item) => {
    if(!mtx[item.y]) mtx[item.y] = Array(cols).fill(null)
    for(let i = item.y; i < item.y + item.h; i++) {
      if(!mtx[i]) mtx[i] = Array(cols).fill(null)
      for(let j = item.x; j < item.x + item.w; j++) {
        mtx[i][j] = item.idx
      }
    }
    return mtx
  }, [])
}

/**
 * Returns a nicely formatted str repr of a 2D matrix
 */
export const mat_to_str = (mtx) => {
  return mtx.reduce((acc, row, i) => {
    row.forEach((id, idx) => {
      const is_last_col = idx === row.length - 1
      const is_last_row = i === mtx.length - 1
      let end = is_last_col ? '\n' : ' '
      if(is_last_row && is_last_col) end = ''
      acc += ((!isNaN(id) && id !== null) ? id : '*') + end
    })
    return acc
  }, '')
}
